import {
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useLazyQuery } from '@apollo/client';
import { indexOf } from '@npi/buddy-utils';
import { PartnerType } from '@prisma/client';
import { useNavigate } from 'react-router-dom';
import { AddMerchantApplication } from 'types/graphql';

import { useParams } from '@redwoodjs/router';
import { useMutation, useQuery } from '@redwoodjs/web';

import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ADD_MERCHANT_STEPS } from 'src/constants/merchants';
import { ADD_MERCHANT } from 'src/graphql/mutations/addMerchant.mutation';
import { SUBMIT_MERCHANT_APPLICATION } from 'src/graphql/mutations/submitMerchantApplication.mutation';
import { GET_APPLICATION } from 'src/graphql/queries/merchantApplications.query';
import { MY_DRAFT_MERCHANT_APPLICATION } from 'src/graphql/queries/myDraftMerchantApplication.query';
import OutletInformation from 'src/pages/AddMerchantPage/steps/OutletInformation';
import { Step } from 'src/types';
import { Outlet } from 'src/types/AddMerchant/merchantInfo';
import { AddMerchantApplicationPayload } from 'types/graphql';

interface AddMerchantContext {
    addMerchant: (input: AddMerchantApplicationInput) => Promise<void>;
    submitMerchantApplication: () => Promise<void>;
    initialValues: {
        partnerId: number;
        serviceRequirements: object;
        merchantInformation: object;
        outletInformation: object;
        businessInformation: object;
        supportingDocuments: object;
    };
    merchantApplication: AddMerchantApplicationPayload;
    serviceApplication: AddMerchantApplication;
    checkForDraftApplication: () => Promise<void>;
    setMerchantApplication: Dispatch<SetStateAction<AddMerchantApplication>>;
    setServiceApplication: Dispatch<SetStateAction<AddMerchantApplication>>;
    findingDraftApplication: boolean;
    findingDraftApplicationById: boolean;
    draftAvailable: boolean;
    steps: Step[];
    setSteps: Dispatch<SetStateAction<Step[]>>;
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    shouldResetForm: boolean;
    setShouldResetForm: Dispatch<SetStateAction<boolean>>;
    hasResetForm: boolean;
    setHasResetForm: Dispatch<SetStateAction<boolean>>;
    setCompletedSteps: () => void;
    handleBack: () => void;
    setIsDraftApplicationId: Dispatch<SetStateAction<number>>;
    selectedOutlet: string;
    isExistingOutlet: boolean;
    fetchingMerchantApplicationForAddService: boolean;
    setSelectedOutlet: Dispatch<SetStateAction<string>>;
    setIsExistingOutlet: Dispatch<SetStateAction<boolean>>;
    addNewOutlet: (outlet: Outlet) => void;
    refetchUpdateMerchantApplication: ({ variables: { id } }) => QueryOperationResult;
    fetchMerchantApplicationForAddService: () => void;
}

const AddMerchantContext = createContext<Partial<AddMerchantContext>>({});

type AddMerchantApplicationInput = {
    partnerId?: number;
    partnerOfPartnerId?: number;
    partnerType?: PartnerType;
    serviceRequirements?: object;
    merchantInformation?: object;
    outletInformation?: object;
    businessInformation?: object;
    supportingDocuments?: object;
    applicationId?: number | null;
    isServiceApplication?: boolean;
    merchantAccountNumber?: string;
    country?: string;
};

export const useAddMerchantContext = () => {
    return useContext(AddMerchantContext);
};

export const AddMerchantProvider = ({ children }) => {
    const { step: isDraftApplicationStep, merchantApplicationId } = useParams();

    const [steps, setSteps] = useState(ADD_MERCHANT_STEPS);
    const [shouldResetForm, setShouldResetForm] = useState<boolean>(false);

    let theStepToGoTo = 0;

    const [hasResetForm, setHasResetForm] = useState(false);
    const [activeStep, setActiveStep] = useState<number>(0);
    const [merchantApplication, setMerchantApplication] =
        useState<Partial<AddMerchantApplicationPayload>>();
    const [serviceApplication, setServiceApplication] = useState<AddMerchantApplication>();

    const [draftAvailable, setDraftAvailable] = useState<boolean>(false);
    const [isDraftApplicationId, setIsDraftApplicationId] = useState<number>(
        merchantApplicationId ? Number(merchantApplicationId) : null
    );

    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [isExistingOutlet, setIsExistingOutlet] = useState<boolean>(true);

    const { data: myDraftApplicationData, loading: findingDraftApplication } = useQuery(
        MY_DRAFT_MERCHANT_APPLICATION,
        {
            skip: isDraftApplicationId !== null,
            fetchPolicy: 'network-only',
            onError: (error) => {
                return showErrorToast(error.message);
            },
            onCompleted: (data) => {
                // Only set the draft application if not starting new application
                if (!hasResetForm && !shouldResetForm) {
                    if (data?.myDraftMerchantApplication?.isServiceApplication) {
                        setServiceApplication(data?.myDraftMerchantApplication);
                    } else {
                        setMerchantApplication(data?.myDraftMerchantApplication);
                    }
                }
            },
        }
    );

    const currentApplicationId = serviceApplication?.id || isDraftApplicationId;

    const {
        data: retrieveMerchantApplicationData,
        loading: findingDraftApplicationById,
        refetch: refetchUpdateMerchantApplication,
    } = useQuery(GET_APPLICATION, {
        fetchPolicy: 'network-only',
        skip: isDraftApplicationId === null,
        variables: {
            id: Number(currentApplicationId),
        },
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: (data) => {
            // Only set the draft application if not starting new application
            if (!hasResetForm && !shouldResetForm) {
                if (data?.retrieveMerchantApplication?.isServiceApplication) {
                    setServiceApplication(data?.retrieveMerchantApplication);
                } else {
                    setMerchantApplication(data?.retrieveMerchantApplication);
                }
            }
        },
    });

    const [
        fetchMerchantApplicationForAddService,
        { loading: fetchingMerchantApplicationForAddService },
    ] = useLazyQuery(GET_APPLICATION, {
        fetchPolicy: 'network-only',
        variables: {
            id: Number(isDraftApplicationId),
        },
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: (data) => {
            setMerchantApplication(data?.retrieveMerchantApplication);
        },
    });

    const [addMerchantMutation, { loading: addingMerchant }] = useMutation(
        ADD_MERCHANT,

        {
            onError: (error) => {
                return showErrorToast(error.message);
            },
            onCompleted: (data) => {
                if (data?.addMerchantApplication?.isServiceApplication) {
                    setServiceApplication(data?.addMerchantApplication);
                } else {
                    setMerchantApplication(data?.addMerchantApplication);
                }
                // Update completed steps
                setSteps((prevState) => {
                    return prevState.map((step) => {
                        return step.accessor === 'partnerId'
                            ? { ...step, completed: true }
                            : {
                                  ...step,
                                  completed:
                                      data?.addMerchantApplication?.[step.accessor]
                                          ?.isStepCompleted,
                              };
                    });
                });
            },
        }
    );

    const [submitMerchantApplicationMutation] = useMutation(SUBMIT_MERCHANT_APPLICATION, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: ({ submitMerchantApplication }) => {
            showSuccessToast('Merchant Submitted.');
            if (!serviceApplication) {
                navigate(`/merchant-applications/view-application/${submitMerchantApplication}`);
            }
        },
    });

    const initialValues = useMemo(() => {
        if (isDraftApplicationId) {
            return {
                partnerId:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication?.partnerId || null,
                partnerOfPartnerId:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication
                        ?.partnerOfPartnerId || null,
                serviceRequirements:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication
                        ?.serviceRequirements || {},
                merchantInformation:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication
                        ?.merchantInformation || {},
                outletInformation:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication
                        ?.outletInformation || {},
                businessInformation:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication
                        ?.businessInformation || {},
                supportingDocuments:
                    retrieveMerchantApplicationData?.retrieveMerchantApplication
                        ?.supportingDocuments || {},
            };
        }

        return {
            partnerId: myDraftApplicationData?.myDraftMerchantApplication?.partnerId || null,
            partnerOfPartnerId:
                myDraftApplicationData?.myDraftMerchantApplication?.partnerOfPartnerId || null,
            serviceRequirements:
                myDraftApplicationData?.myDraftMerchantApplication?.serviceRequirements || {},
            merchantInformation:
                myDraftApplicationData?.myDraftMerchantApplication?.merchantInformation || {},
            outletInformation:
                myDraftApplicationData?.myDraftMerchantApplication?.outletInformation || {},
            businessInformation:
                myDraftApplicationData?.myDraftMerchantApplication?.businessInformation || {},
            supportingDocuments:
                myDraftApplicationData?.myDraftMerchantApplication?.supportingDocuments || {},
        };
    }, [myDraftApplicationData]);

    const navigate = useNavigate();

    const handleNext = useCallback(() => {
        setActiveStep((prevActiveStep) => {
            return prevActiveStep + 1;
        });
    }, [setActiveStep]);

    const handleBack = useCallback(() => {
        setActiveStep((prevActiveStep) => {
            return prevActiveStep - 1;
        });
    }, []);

    // Non-compulsory step
    const OUTLET_INFO_STEP = useMemo(() => {
        return {
            label: 'Outlet Information',
            completed: false,
            accessor: 'outletInformation',
            form: <OutletInformation key="outlet-information" handleBack={handleBack} />,
        };
    }, [handleBack]);

    // use the number of outlets from the in-progress application data (addMerchantApplication) to set the outlet state which will determine whether to render the outlet step or not
    useEffect(() => {
        if (merchantApplication && (!findingDraftApplication || !findingDraftApplicationById)) {
            if (merchantApplication?.merchantInformation) {
                const numberOfOutlets =
                    merchantApplication?.merchantInformation?.numberOfOutlets ?? 1;
                if (numberOfOutlets > 1 && steps.length === ADD_MERCHANT_STEPS.length) {
                    setSteps((prevSteps) => {
                        const newSteps = [
                            ...prevSteps.slice(0, 3),
                            {
                                ...OUTLET_INFO_STEP,
                                completed:
                                    merchantApplication?.outletInformation?.isStepCompleted ??
                                    false,
                            },
                            ...prevSteps.slice(3),
                        ];
                        return newSteps;
                    });
                } else if (numberOfOutlets <= 1 && steps.length > ADD_MERCHANT_STEPS.length) {
                    setSteps((prevState) => {
                        return prevState.length !== ADD_MERCHANT_STEPS.length
                            ? ADD_MERCHANT_STEPS
                            : prevState;
                    });
                }
            }
        }
    }, [
        merchantApplication,
        findingDraftApplication,
        findingDraftApplicationById,
        steps,
        OUTLET_INFO_STEP,
    ]);

    const addMerchant = async (input: AddMerchantApplicationInput) => {
        // Form has been reset on step 1 and new application will now be created
        if (hasResetForm) {
            // On step 2 we will fetch the new application which has no data so upcoming forms will have been reset
            setHasResetForm(false);
        }
        await addMerchantMutation({
            variables: {
                input: {
                    applicationId: input?.isServiceApplication
                        ? (serviceApplication?.id ?? null)
                        : (merchantApplication?.id ?? null),
                    ...input,
                },
            },
        });

        // Only fire the onComplete call back once the mutation is completed and when it is not the last step
        if (!addingMerchant && activeStep !== steps.length - 1) {
            handleNext();
        }
    };

    const submitMerchantApplication = async () => {
        await submitMerchantApplicationMutation({
            variables: { applicationId: merchantApplication.id },
        });
    };

    const checkForDraftApplication = useCallback(async () => {
        const data = myDraftApplicationData;
        const data2 = retrieveMerchantApplicationData;

        if (isDraftApplicationId) {
            if (data2 && data2?.retrieveMerchantApplication) {
                setDraftAvailable(true);
            } else {
                setDraftAvailable(false);
            }
        } else {
            if (data && data?.myDraftMerchantApplication) {
                setDraftAvailable(true);
            } else {
                setDraftAvailable(false);
            }
        }
    }, [isDraftApplicationId, myDraftApplicationData, retrieveMerchantApplicationData]);

    const setCompletedSteps = useCallback(() => {
        setSteps((prevState) => {
            const updatedCompletedSteps = prevState.map((step) => {
                return step.accessor === 'partnerId'
                    ? { ...step, completed: true }
                    : {
                          ...step,
                          completed: isDraftApplicationId
                              ? retrieveMerchantApplicationData?.retrieveMerchantApplication?.[
                                    step.accessor
                                ]?.isStepCompleted
                              : myDraftApplicationData?.myDraftMerchantApplication?.[step.accessor]
                                    ?.isStepCompleted,
                      };
            });

            const firstUncompletedStep = updatedCompletedSteps.find((step) => {
                return !step.completed;
            });
            const firstUncompletedStepIndex = indexOf(updatedCompletedSteps, firstUncompletedStep);

            if (isDraftApplicationStep) {
                theStepToGoTo = Number(isDraftApplicationStep);
                setActiveStep(theStepToGoTo);
                const currentUrl = new URL(window.location.href);

                currentUrl.searchParams.delete('step');
                navigate(currentUrl.pathname + currentUrl.search);
            } else {
                setActiveStep(firstUncompletedStepIndex);
            }

            return updatedCompletedSteps;
        });
    }, [
        setActiveStep,
        myDraftApplicationData,
        retrieveMerchantApplicationData,
        isDraftApplicationId,
    ]);

    const addNewOutlet = (newOutlet: Outlet) => {
        setMerchantApplication((prevState) => {
            if (!prevState) {
                return {
                    ...prevState,
                    merchantInformation: {
                        ...prevState?.merchantInformation,
                        outlets: [newOutlet],
                    },
                };
            }

            return {
                ...prevState,
                merchantInformation: {
                    ...prevState?.merchantInformation,
                    outlets: [...(prevState?.merchantInformation?.outlets || []), newOutlet],
                },
            };
        });
        setSelectedOutlet(newOutlet.name);
    };

    useEffect(() => {
        // Update draft application data on each step change
        if (!shouldResetForm && !hasResetForm) {
            checkForDraftApplication();
        }
    }, [activeStep, hasResetForm, shouldResetForm, checkForDraftApplication]);

    return (
        <AddMerchantContext.Provider
            value={{
                addMerchant,
                submitMerchantApplication,
                initialValues,
                merchantApplication,
                checkForDraftApplication,
                setMerchantApplication,
                findingDraftApplication,
                findingDraftApplicationById,
                draftAvailable,
                steps,
                setSteps,
                activeStep,
                setActiveStep,
                handleBack,
                shouldResetForm,
                setShouldResetForm,
                hasResetForm,
                setHasResetForm,
                setCompletedSteps,
                setIsDraftApplicationId,
                serviceApplication,
                selectedOutlet,
                isExistingOutlet,
                setSelectedOutlet,
                setIsExistingOutlet,
                addNewOutlet,
                refetchUpdateMerchantApplication,
                setServiceApplication,
                fetchMerchantApplicationForAddService,
                fetchingMerchantApplicationForAddService,
            }}
        >
            {children}
        </AddMerchantContext.Provider>
    );
};
